import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, Check, ChevronDown, Heart, Minus, Plus, Ruler, Share2, ShieldCheck, Truck, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useStore } from "@/context/StoreContext";
import { inr, fmtDateTime } from "@/lib/format";
import { emit, trackAffinity } from "@/lib/events";
import { srcsetFor } from "@/lib/srcset";
import { colourVariantLabel, purityLabel, stoneLabel } from "@/lib/labels";
import { pushRecent, getRecents } from "@/lib/recents";
import ProductCard from "@/components/store/ProductCard";
import PriceBreakup from "@/components/store/PriceBreakup";
import { BreadcrumbJsonLd, useOgTags } from "@/components/store/SeoJsonLd";
import Personalisation, { FONT_MAP } from "@/components/store/Personalisation";
import Reviews from "@/components/store/Reviews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const COLOURS = [
  { name: "Yellow", hex: "#E4C465" },
  { name: "Rose", hex: "#E7B4A0" },
  { name: "White", hex: "#D9D9D9" },
  { name: "Silver", hex: "#C9CCD1" },
];
const SIZE_KEY = "siyaara_ring_size";
const STORES_OK = ["silver", "gold"];

// Approximate conversions — Indian size N: diameter ≈ 12.68 + 0.32N mm
const ringRow = (n) => {
  const d = 12.68 + 0.32 * n;
  const c = Math.PI * d;
  const us = Math.round((n / 2) * 2) / 2;
  const ukIdx = Math.max(0, Math.round((d - 14.05) / 0.36));
  const uk = String.fromCharCode(70 + Math.min(ukIdx, 20)); // F onwards
  return { n, d: d.toFixed(1), c: c.toFixed(0), us, uk };
};

const RING_FAQS = [
  ["Are lab-grown diamonds real diamonds?",
   "Yes — identical in chemistry, hardness and sparkle to mined diamonds, grown above ground instead of extracted. Ours are certified by an independent gemmological laboratory, and the certificate travels with your ring."],
  ["How is the price of this ring calculated?",
   "Transparently, from live inputs: gold weight × today's gold rate at your chosen purity, plus the diamonds at a fixed per-carat rate, plus a making charge, plus 3% GST. Tap \"View price breakup\" to see every line. Because rates move, the price can change until you add the ring to your cart — from that moment it is held for you."],
  ["I don't know my ring size. What should I do?",
   "Use the size guide beside the selector — it shows two easy home methods. If you're still unsure, order your best guess: resizing later is assessed per piece — we'll always quote before any work."],
  ["9KT or 14KT — which should I choose?",
   "Both are hallmarked gold. 9KT (37.5% pure) is harder-wearing and gentler on the pocket; 14KT (58.5% pure) carries more gold and a richer colour. The price difference for each is shown right on the selector."],
  ["How do I care for the ring?",
   "Warm water, a drop of mild soap and a soft brush, then pat dry. Keep it away from chlorine and perfume, and store it in the pouch it arrives in. Lab-grown diamonds need no special care beyond this."],
  ["What if I need to return it?",
   "Unworn rings in original packaging with the certificate can be returned within the return window on our policy page. Personalised or resized pieces are made for you and cannot be returned."],
];

export default function Product() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem, packaging, setPackaging } = useCart();
  const { config, store, setStore } = useStore();
  const { isWishlisted, toggle } = useWishlist();
  const [product, setProduct] = useState(null);
  const [selected, setSelected] = useState({});
  const [ringSize, setRingSize] = useState(() => sessionStorage.getItem(SIZE_KEY) || "");
  const [qty, setQty] = useState(1);
  const [imgIdx, setImgIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [sizeGuide, setSizeGuide] = useState(false);
  const [pincode, setPincode] = useState("");
  const [delivery, setDelivery] = useState(null);
  const [checking, setChecking] = useState(false);
  const [pers, setPers] = useState({ selections: {}, font: "Serif", summary: "", surcharge: 0 });
  const [design, setDesign] = useState("");
  const [recents, setRecents] = useState([]);
  const [faqOpen, setFaqOpen] = useState(0);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [notifyContact, setNotifyContact] = useState("");
  const [notifyConsent, setNotifyConsent] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);

  const submitNotify = async (e) => {
    e.preventDefault();
    try {
      await api.post("/notify-stock", {
        product_id: product.id,
        variant_label: Object.values(variant?.options || {}).join(" / "),
        contact: notifyContact, consent: notifyConsent,
      });
      setNotifyDone(true);
    } catch {
      toast.error("Could not save that — please try again.");
    }
  };

  // Gallery videos: at most one by default; preload="none" means nothing is
  // fetched until the shopper presses play. Extras sit behind "view more".
  const productVideos = useMemo(() => {
    if (!product || typeof product !== "object") return [];
    const out = [];
    if (product.video_url) out.push({ url: product.video_url, poster: product.video_poster || "" });
    for (const val of Object.values(product.video_sets || {})) {
      const push = (x) => {
        if (!x) return;
        if (typeof x === "string") out.push({ url: x, poster: "" });
        else if (x.url) out.push({ url: x.url, poster: x.poster || "" });
      };
      if (Array.isArray(val)) val.forEach(push); else push(val);
    }
    return out.filter((v) => v.url);
  }, [product]);

  useEffect(() => {
    setProduct(null); setSelected({}); setQty(1); setImgIdx(0); setDelivery(null);
    setPers({ selections: {}, font: "Serif", summary: "", surcharge: 0 });
    setDesign("");
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      if (r.data.designs?.length) setDesign(r.data.designs[0]);
      pushRecent(r.data);
      setRecents(getRecents(r.data.id));
      emit("view_item", {
        value: r.data.price_min, currency: "INR",
        items: [{ item_id: r.data.variants?.[0]?.sku || r.data.slug, item_name: r.data.title,
                  category: r.data.category, metal: r.data.metal, purity: r.data.purity,
                  price: r.data.price_min, quantity: 1 }],
      });
      trackAffinity(r.data, "view");
    }).catch(() => setProduct(false));
  }, [slug]);

  // Keep the Silver/Gold/Coins storefront toggle in sync with the loaded product
  useEffect(() => {
    if (product?.metal && product.product_type !== "coin" && STORES_OK.includes(product.metal) && store !== product.metal) {
      setStore(product.metal);
    }
  }, [product]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!product || !product.id) return;
    document.title = `${product.seo_title || product.title} | Siyaara`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
    meta.content = product.seo_description || product.description;
    const ld = {
      "@context": "https://schema.org", "@type": "Product",
      name: product.title, description: product.seo_description || product.description,
      image: product.images, brand: { "@type": "Brand", name: "Siyaara" },
      sku: variant?.sku || product.slug,
      mpn: variant?.sku || product.slug,          // handmade jewellery has no GTIN
      material: `${purity} ${product.metal === "silver" ? "silver" : "gold"}`,
      ...(colour && { color: `${colour} gold` }),
      ...(isRing && ringSize && ringSize !== "later" && { size: ringSize }),
      ...(variant?.net_weight && { weight: { "@type": "QuantitativeValue", value: variant.net_weight, unitCode: "GRM" } }),
      category: product.category,
      offers: {
        "@type": "Offer", priceCurrency: "INR", price: product.price_min,
        availability: product.computed || product.variants?.some((v) => v.stock > 0) ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        url: `https://siyaara.com/products/${product.slug}`,
      },
      ...(product.rating_count > 0 && {
        ...(product.rating_count > 0 ? { aggregateRating: { "@type": "AggregateRating", ratingValue: product.rating_avg, reviewCount: product.rating_count } } : {}),
      }),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "product-jsonld";
    script.text = JSON.stringify(ld);
    document.getElementById("product-jsonld")?.remove();
    document.head.appendChild(script);
    // FAQPage structured data from the tag-assigned FAQ library
    document.getElementById("faq-jsonld")?.remove();
    if (product.faqs?.length) {
      const faqScript = document.createElement("script");
      faqScript.type = "application/ld+json";
      faqScript.id = "faq-jsonld";
      faqScript.text = JSON.stringify({
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: product.faqs.map((f) => ({
          "@type": "Question", name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      });
      document.head.appendChild(faqScript);
    }
    return () => { document.getElementById("product-jsonld")?.remove(); document.getElementById("faq-jsonld")?.remove(); };
  }, [product]);

  const sizeList = product?.computed ? (product.computed.sizes || []) : (product?.available_sizes || []);
  const isRing = product?.category === "Rings" && sizeList.length > 0;

  useEffect(() => {
    if (product?.computed) {
      const offered = product.computed.offered || [];
      const first = offered.find((o) => o.tier === "stock") || offered[0];
      setSelected((s) => ({
        ...s,
        Purity: s.Purity || first?.purity || "",
        "Metal Colour": s["Metal Colour"] || (product.computed.colours || [])[0] || "",
      }));
    } else if (product?.options?.length) {
      const defaults = {};
      product.options.forEach((o) => { defaults[o.name] = o.values[0]; });
      setSelected(defaults);
    }
  }, [product]);

  // OG/Twitter tags per product (image override = primary gallery image)
  useOgTags(product && product.id ? {
    title: product.seo_title || `${product.title} | Siyaara`,
    description: product.seo_description || product.description,
    image: (product.images || [])[0],
    url: `https://siyaara.com/products/${product.slug}`,
    type: "product",
  } : {});

  const variant = useMemo(() => {
    if (!product) return null;
    if (product.computed) {
      // Computed-variant PDP: the selection IS the variant — synthesised from
      // the offer grid, never a stored row.
      const off = (product.computed.offered || []).find((o) => o.purity === selected.Purity);
      if (!off) return null;
      const col = selected["Metal Colour"] || "";
      const colourCode = { Yellow: "YG", Rose: "RG", White: "WG", Silver: "925" }[col] || "";
      const sizePart = ringSize && ringSize !== "later" ? String(ringSize) : "";
      return {
        id: "computed",
        sku: [product.design_number || (product.slug || "").toUpperCase(), off.purity, colourCode, sizePart].filter(Boolean).join("-"),
        options: { Purity: off.purity, ...(col ? { "Metal Colour": col } : {}), ...(sizePart ? { Size: sizePart } : {}) },
        pricing: off.breakup,
        net_weight: off.weight,
        gross_weight: 0,
        stock: 99,
        _tier: off.tier,
        _lead: off.tier === "made_to_order" ? product.computed.mto_lead_time : "",
      };
    }
    const variants = product.variants || [];
    if (!product.options?.length) return variants[0];
    return variants.find((v) =>
      product.options.every((o) => !selected[o.name] || v.options?.[o.name] === selected[o.name])
    ) || null;
  }, [product, selected, ringSize]);

  if (product === null) return <div className="container-x py-20 text-center text-stone-400 font-mono text-sm">LOADING…</div>;
  if (product === false) return <div className="container-x py-20 text-center" data-testid="product-not-found">Piece not found.</div>;

  const colour = selected["Metal Colour"] || "";
  const purity = selected["Purity"] || product.purity;
  // Colour-linked galleries. 925 silver is rhodium-plated and SHARES the White
  // image set unless a Silver/925 override set exists on the product.
  const sets = product.image_sets || {};
  const mediaColour = (purity === "925" || colour === "Silver")
    ? (sets.Silver?.length ? "Silver" : "White")
    : colour;
  const images = (mediaColour && sets[mediaColour]?.length ? sets[mediaColour] : product.images) || [];
  const inStock = product.computed ? !!variant : (variant && variant.stock > 0);
  const breakup = variant?.pricing;
  const gstRate = product.rate_info?.gst_rate ?? 3;
  const persSurcharge = pers.surcharge || 0;
  const displayFinal = breakup
    ? (breakup.overridden || breakup.mrp)
      ? breakup.final
      : Math.round(((breakup.subtotal + persSurcharge) * (1 + gstRate / 100)) * 100) / 100
    : 0;
  const persActive = Object.values(pers.selections || {}).some(Boolean);
  const previewText = pers.selections?.engraving || "";
  const rateInfo = product.rate_info || {};
  const stone = variant?.stones?.length
    ? { type: variant.stones[0].type, carats: variant.stones.reduce((s, x) => s + (x.weight_carat || 0), 0), rate: variant.stones[0].rate_per_carat }
    : null;
  const attrs = product.attributes || {};
  const purityOptions = (product.options || []).find((o) => o.name === "Purity");
  const priceForPurity = (pv) => {
    const v = (product.variants || []).find((x) => x.options?.Purity === pv && (!colour || x.options?.["Metal Colour"] === colour));
    return v?.pricing?.display_price ?? null;
  };
  const purityChoices = product.computed
    ? (product.computed.offered || []).map((o) => ({ value: o.purity, price: o.price, tier: o.tier }))
    : (purityOptions ? purityOptions.values.map((pv) => ({ value: pv, price: priceForPurity(pv) })) : []);
  const colourChoices = product.computed
    ? COLOURS.filter((c) => (product.computed.colours || []).includes(c.name))
    : COLOURS.filter((c) => (product.variants || []).some((v) => v.options?.["Metal Colour"] === c.name));
  const emiMin = (config?.emi_tenures || []).length && displayFinal >= (config?.emi_threshold || 1e12)
    ? Math.ceil(displayFinal / Math.max(...config.emi_tenures) / 10) * 10
    : 0;

  const pickSize = (s) => {
    setRingSize(s);
    sessionStorage.setItem(SIZE_KEY, s);
  };

  const checkPincode = async () => {
    setChecking(true);
    try {
      const r = await api.post("/shipping/serviceability", { pincode, cod: true, lead_days: product.made_to_order_days || 0 });
      setDelivery(r.data);
    } catch { setDelivery({ available: false }); } finally { setChecking(false); }
  };

  const persPayload = persActive ? { selections: pers.selections, summary: pers.summary, surcharge: pers.surcharge } : null;
  const needSize = isRing && !ringSize;

  const tryAdd = (thenGo) => {
    if (!variant || !inStock) return;
    if (needSize) {
      toast.error("Pick a ring size first — or choose \"Decide later\".");
      return;
    }
    addItem(product, variant, qty, persPayload, design, ringSize === "later" ? "" : ringSize);
    if (thenGo) navigate("/checkout");
  };

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: product.title, url }); } catch { /* dismissed */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    }
  };

  return (
    <div className="container-x py-8 pb-24 lg:pb-8" data-testid="product-page">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Gallery — sticky on desktop while the details column scrolls */}
        <div className={`${config?.homepage?.sticky_pdp_media !== false ? "lg:sticky lg:top-24" : ""} self-start`}>
          <div className="relative border border-stone-200 bg-stone-50 aspect-square overflow-hidden group cursor-zoom-in" onClick={() => setZoom(true)}>
            {images[imgIdx] ? (
              <img src={images[imgIdx]} alt={`${product.title}${colour ? ` in ${colour.toLowerCase()} gold` : ""}`}
                {...(srcsetFor((product.image_sizes || [])[imgIdx]) ? { srcSet: srcsetFor(product.image_sizes[imgIdx]), sizes: "(max-width: 1024px) 100vw, 50vw" } : {})}
                loading="eager" className="h-full w-full object-cover" data-testid="product-main-image" />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-stone-300 font-mono text-xs">NO IMAGE</div>
            )}
            <span className="absolute bottom-3 right-3 bg-white/90 backdrop-blur border border-stone-200 p-2 text-stone-500"><ZoomIn size={16} /></span>
            {previewText && (
              <div className="absolute inset-x-0 bottom-8 flex justify-center pointer-events-none" data-testid="pers-live-preview">
                <span className="bg-white/70 backdrop-blur-sm px-4 py-1.5 text-2xl text-stone-800 shadow-sm" style={{ fontFamily: FONT_MAP[pers.font] || FONT_MAP.Serif }}>
                  {previewText}
                </span>
              </div>
            )}
            {product.personalised && !previewText && (
              <span className="absolute top-3 left-3 bg-white/90 backdrop-blur border border-stone-200 text-[10px] font-mono uppercase tracking-widest px-2 py-1 text-brand">Personalisable</span>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {images.map((img, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`border shrink-0 ${i === imgIdx ? "border-stone-900" : "border-stone-200"} h-16 w-16 overflow-hidden`} data-testid={`product-thumb-${i}`}>
                  <img src={img} alt="" loading="lazy"
                    {...(srcsetFor((product.image_sizes || [])[i]) ? { srcSet: srcsetFor(product.image_sizes[i]), sizes: "64px" } : {})}
                    className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {/* Awaiting photography — own it, and still collect the lead */}
          {!images.length && !Object.keys(product.image_sets || {}).length && (
            <div className="mt-3 border border-stone-200 bg-white rounded-lg p-4" data-testid="photography-pending">
              <p className="text-xs text-stone-600">Photography for this piece is on its way — the design, weights and pricing are final. Leave your details and we'll send photos the moment they land.</p>
              {notifyDone ? (
                <p className="text-sm text-emerald-700 mt-2" data-testid="photo-notify-done">Done — you're on the list.</p>
              ) : (
                <form className="mt-2 flex gap-2" onSubmit={submitNotify}>
                  <Input value={notifyContact} onChange={(e) => setNotifyContact(e.target.value)} placeholder="Email or WhatsApp" className="h-9 text-sm" data-testid="photo-notify-contact" required />
                  <Button type="submit" variant="outline" size="sm" className="rounded-full h-9 btn-press" data-testid="photo-notify-submit"
                    onClick={() => setNotifyConsent(true)}>Send me photos</Button>
                </form>
              )}
            </div>
          )}
          {productVideos.length > 0 && (
            <div className="mt-4 space-y-3" data-testid="product-videos">
              {(showAllVideos ? productVideos : productVideos.slice(0, 1)).map((v, i) => (
                <div key={i} className="relative border border-stone-200 bg-black" data-testid={`product-video-${i}`}>
                  <video src={v.url} poster={v.poster || undefined} preload="none" controls playsInline muted className="w-full max-h-[70vh]" />
                </div>
              ))}
              {productVideos.length > 1 && (
                <button onClick={() => setShowAllVideos(!showAllVideos)} className="text-xs text-brand underline underline-offset-4" data-testid="product-videos-more">
                  {showAllVideos ? "Show fewer videos" : `View ${productVideos.length - 1} more video${productVideos.length > 2 ? "s" : ""}`}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Buy panel */}
        <div className="self-start">
          <nav className="text-[11px] font-mono text-stone-400 uppercase tracking-widest" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-brand">Home</Link> / <Link to="/shop" className="hover:text-brand">Shop</Link> / <span className="text-stone-600">{product.category}</span>
          </nav>
          <BreadcrumbJsonLd items={[{ name: "Home", url: "/" }, { name: "Shop", url: "/shop" }, { name: product.title, url: `/products/${product.slug}` }]} />
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-2" data-testid="product-title">{product.title}</h1>
          {product.rating_count > 0 && (
            <p className="text-sm text-stone-500 mt-1">{"★".repeat(Math.round(product.rating_avg))} {product.rating_avg} · {product.rating_count} review(s)</p>
          )}

          <div className="flex items-baseline gap-3 mt-4 min-h-[2.5rem]">
            <span className="font-heading text-3xl font-semibold transition-opacity" data-testid="product-price">{inr(displayFinal)}</span>
            {(variant?.legacy_compare_at_price > displayFinal || (breakup?.compare_at || 0) > displayFinal) && (
              <span className="text-sm text-stone-400 line-through" data-testid="product-mrp">{inr(Math.max(variant?.legacy_compare_at_price || 0, breakup?.compare_at || 0))}</span>
            )}
            <span className="text-xs font-mono text-stone-500">INCLUSIVE OF ALL TAXES</span>
          </div>
          {!breakup?.mrp && rateInfo.updated_at && (
            <p className="text-[11px] font-mono text-stone-500 mt-1" data-testid="rate-line">
              RATE AS ON {fmtDateTime(rateInfo.updated_at)} · {breakup?.purity || purity} {inr(breakup?.purity_rate_per_gram || rateInfo.purity_rate_per_gram)}/g
            </p>
          )}
          {emiMin > 0 && <p className="text-xs text-stone-500 mt-1" data-testid="emi-line">or EMI from {inr(emiMin)}/mo</p>}
          {product.social?.sold_7d > 0 && (
            <p className="text-xs font-mono text-gold mt-1" data-testid="sold-7d-line">{product.social.sold_7d} sold in the last 7 days</p>
          )}

          <div className="mt-4">
            {!breakup?.mrp && <PriceBreakup breakup={breakup} surcharge={persSurcharge} gstRate={gstRate} rateAt={rateInfo.updated_at} personalisationLabel={persActive ? "selected" : ""} stone={stone} colour={colour} />}
          </div>

          {(product.qty_offers || []).length > 0 && (
            <div className="mt-4 space-y-2" data-testid="qty-offers">
              <p className="eyebrow">BUNDLE &amp; SAVE</p>
              {(product.qty_offers || []).map((o) => {
                const n = o.buy_n || 2;
                const unit = displayFinal;
                const saving = o.type === "bogo" ? unit * (o.get_m || 1)
                  : o.type === "pct_off" ? (unit * n * (o.pct || 0)) / 100
                  : unit * n - (o.fixed_total || 0);
                const effUnit = Math.max(0, (unit * n - saving) / n);
                const label = o.type === "bogo" ? `Buy ${n}, get ${o.get_m || 1} free`
                  : o.type === "fixed_total" ? `Buy ${n} for ${inr(o.fixed_total || 0)}`
                  : `Buy ${n}, get ${o.pct}% off`;
                return (
                  <button key={o.id} onClick={() => setQty(n)}
                    className="w-full flex items-center justify-between gap-3 border border-dashed border-gold/60 bg-ivory rounded-lg px-4 py-3 text-left hover:border-gold transition-colors"
                    data-testid={`qty-offer-${o.id}`}>
                    <span className="text-sm font-medium text-brand">{label}</span>
                    <span className="text-xs font-mono text-stone-500">
                      {saving > 0 ? `save ${inr(Math.round(saving))} · ` : ""}{inr(Math.round(effUnit))}/pc · auto-applied
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Ring configurator */}
          {isRing && (
            <div className="mt-6 space-y-6" data-testid="ring-configurator">
              {/* Metal colour */}
              {colourChoices.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Metal colour: <span className="font-mono text-stone-500">{colourVariantLabel(purity, colour, config)}</span></p>
                  <div className="flex gap-3" role="radiogroup" aria-label="Metal colour">
                    {colourChoices.map((c) => {
                      const active = colour === c.name;
                      return (
                        <button key={c.name} role="radio" aria-checked={active}
                          onClick={() => { setSelected((s) => ({ ...s, "Metal Colour": c.name })); setImgIdx(0); }}
                          className={`flex flex-col items-center gap-1.5 group focus-visible:outline-2 focus-visible:outline-brand`}
                          data-testid={`colour-${c.name.toLowerCase()}`}>
                          <span className={`h-9 w-9 rounded-full border-2 transition-all ${active ? "border-brand scale-110" : "border-stone-300 group-hover:border-stone-500"}`} style={{ background: c.hex }} />
                          <span className={`text-[11px] ${active ? "text-brand font-medium" : "text-stone-500"}`}>{colourVariantLabel(purity, c.name, config)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Purity — segmented control with per-option prices */}
              {purityChoices.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2">Purity</p>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Purity">
                    {purityChoices.map((pc) => {
                      const active = purity === pc.value;
                      return (
                        <button key={pc.value} role="radio" aria-checked={active}
                          onClick={() => setSelected((s) => ({ ...s, Purity: pc.value }))}
                          className={`border rounded-lg px-4 py-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-brand ${active ? "border-brand bg-brand text-ivory" : "border-stone-300 bg-white hover:border-brand/60"}`}
                          data-testid={`purity-${pc.value.toLowerCase()}`}>
                          <span className="block text-sm font-semibold">{pc.value === "925" ? "925 Silver" : `${pc.value} Gold`}</span>
                          <span className={`block text-xs font-mono mt-0.5 ${active ? "text-ivory/80" : "text-stone-500"}`}>{pc.price != null ? inr(pc.price) : "—"}</span>
                          {pc.tier === "made_to_order" && (
                            <span className={`block text-[10px] font-mono uppercase tracking-wider mt-1 ${active ? "text-ivory/70" : "text-amber-700"}`}>Made to order</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Ring size */}
              {product.computed && variant?._tier === "made_to_order" && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 mb-3" data-testid="mto-return-note">
                  {product.computed.mto_return_note}
                </p>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">{`Ring size${ringSize && ringSize !== "later" ? `: ${ringSize}` : ""}`}</p>
                  <button className="text-xs text-stone-500 underline flex items-center gap-1" onClick={() => setSizeGuide(true)} data-testid="size-guide-btn">
                    <Ruler size={12} /> Size guide
                  </button>
                </div>
                <select value={ringSize} onChange={(e) => pickSize(e.target.value)}
                  className="w-full h-11 border border-stone-300 rounded-lg bg-white px-3 text-sm focus-visible:outline-2 focus-visible:outline-brand"
                  data-testid="size-select" aria-label="Ring size">
                  <option value="">Select size (Indian 5–30)</option>
                  {sizeList.map((s) => <option key={s} value={s}>Size {s}</option>)}
                  <option value="later">Not sure — decide later</option>
                </select>
                {ringSize === "later" ? (
                  <p className="text-xs text-stone-500 mt-1.5" data-testid="size-later-note">
                    No problem — we'll confirm your size before crafting. Resizing later is assessed per piece — we'll quote before any work.
                  </p>
                ) : (
                  <p className="text-xs text-stone-500 mt-1.5">Not sure of your size? Check the guide, or choose “decide later” — resizing is assessed per piece if you need it.</p>
                )}
              </div>
            </div>
          )}

          {/* Generic variant selectors (non-ring products) */}
          {!isRing && (product.options || []).map((opt) => (
            <div key={opt.name} className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">{opt.name}: <span className="font-mono text-stone-500">{selected[opt.name]}</span></p>
                {opt.name === "Size" && (
                  <button className="text-xs text-stone-500 underline flex items-center gap-1" onClick={() => setSizeGuide(true)} data-testid="size-guide-btn">
                    <Ruler size={12} /> Size guide
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {opt.values.map((val) => {
                  const active = selected[opt.name] === val;
                  const v = (product.variants || []).find((x) =>
                    x.options?.[opt.name] === val &&
                    product.options.every((o) => o.name === opt.name || !selected[o.name] || x.options?.[o.name] === selected[o.name])
                  );
                  const out = v && v.stock <= 0;
                  return (
                    <button key={val} onClick={() => setSelected((s) => ({ ...s, [opt.name]: val }))}
                      className={`px-4 py-2 text-sm border transition-colors btn-press ${active ? "border-stone-900 bg-stone-900 text-white" : "border-stone-300 hover:border-stone-900"} ${out ? "opacity-40 line-through" : ""}`}
                      data-testid={`variant-${opt.name.toLowerCase()}-${String(val).toLowerCase().replace(/[\s.]+/g, "-")}`}>
                      {val}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Coin design (motif) selector */}
          {product.designs?.length > 0 && (
            <div className="mt-5" data-testid="design-selector">
              <p className="text-sm font-medium mb-2">Design: <span className="font-mono text-stone-500">{design}</span></p>
              <div className="border border-stone-200 rounded-xl max-h-44 overflow-y-auto bg-white">
                {(product.motif_groups || []).map((g) => {
                  const motifs = g.motifs.filter((m) => product.designs.includes(m));
                  if (!motifs.length) return null;
                  return (
                    <div key={g.group} className="px-3 py-2">
                      <p className="eyebrow !text-[10px] mb-1.5">{g.group.toUpperCase()}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {motifs.map((m) => (
                          <button key={m} onClick={() => setDesign(m)}
                            className={`px-3 py-1 text-xs rounded-full border transition-colors btn-press ${design === m ? "bg-brand text-ivory border-brand" : "border-stone-300 hover:border-brand"}`}
                            data-testid={`design-${m.toLowerCase().replace(/[\s/]+/g, "-")}`}>
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Also available in — sibling designs at the same weight, distinct URLs */}
          {product.linked_designs?.length > 0 && (
            <div className="mt-6" data-testid="linked-designs">
              <p className="text-sm font-medium mb-2">Also available in</p>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {product.linked_designs.map((d) => (
                  <Link key={d.slug} to={`/products/${d.slug}`} className="shrink-0 w-20 group" data-testid={`linked-design-${d.slug}`}>
                    <div className="h-20 w-20 border border-stone-200 rounded-lg overflow-hidden bg-ivory group-hover:border-brand transition-colors">
                      {d.image
                        ? <img src={d.image} alt={d.label} loading="lazy" className="h-full w-full object-cover" />
                        : <div className="h-full w-full grid place-items-center text-stone-300 font-mono text-[10px]">SIYAARA</div>}
                    </div>
                    <p className="text-[11px] text-stone-600 mt-1 text-center line-clamp-1 group-hover:text-brand">{d.label}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Trust badges */}
          <div className="grid grid-cols-2 gap-2 mt-6" data-testid="trust-grid">
            {(() => {
              const bisOk = Boolean(config?.legal?.bis_reg);
              const isCoin = ["coin", "coin_set"].includes(product.product_type);
              const rw = config?.return_windows || {};
              const xw = config?.exchange_windows || {};
              const bb = config?.buyback_terms || {};
              const bbPct = product.metal === "silver" ? bb.silver_pct : bb.gold_pct;
              const ret = rw.jewellery ? `${rw.jewellery}-day returns · ${xw.jewellery || 10}-day exchange` : "Easy returns";
              const items = isCoin
                ? [...(bisOk && product.metal === "gold" && product.bis_applicable ? ["BIS Hallmarked"] : []),
                   "999 Fine — assay certified", "Certificate with QR verification",
                   "Tamper-proof blister pack",
                   ...(bbPct ? ["Lifetime buyback at live rate"] : ["Lifetime exchange"])].slice(0, 4)
                : isRing
                  ? ["Independently certified diamonds", ...(bisOk ? ["BIS hallmarked gold"] : []),
                     ret, "Resize assessed per piece"].slice(0, 4)
                  : [...(bisOk && product.metal === "gold" ? ["BIS Hallmarked"] : []),
                     product.metal === "silver" ? "925 Sterling silver" : "Certified purity",
                     ret, ...(bbPct ? ["Lifetime exchange & buyback"] : ["Lifetime exchange"])];
              return items.map((t) => (
                <div key={t} className="flex items-center gap-2 border border-brand/10 bg-white rounded-lg px-3 py-2.5">
                  <Check size={14} className="text-gold shrink-0" />
                  <span className="text-xs text-ink-2">{t}</span>
                </div>
              ));
            })()}
          </div>

          {/* Stock / made to order */}
          <div className="mt-5 space-y-1" data-testid="stock-status">
            {inStock ? (
              <p className="text-sm font-medium text-emerald-700 flex items-center gap-1"><Check size={15} /> {variant._tier === "made_to_order" ? "Made to order — crafted for you" : "In stock"}{!product.computed && variant.stock <= 5 ? ` — only ${variant.stock} left` : ""}</p>
            ) : (
              <div className="border border-stone-200 bg-white rounded-lg p-4" data-testid="notify-panel">
                <p className="text-sm font-medium text-red-600">This configuration is out of stock</p>
                {(product.variants || []).some((v) => v.stock > 0) && (
                  <p className="text-xs text-stone-500 mt-1">Try another colour or purity above — several are ready to ship.</p>
                )}
                {notifyDone ? (
                  <p className="text-sm text-emerald-700 mt-2" data-testid="notify-done">Noted — we'll tell you the moment it's back.</p>
                ) : (
                  <form className="mt-3 space-y-2" onSubmit={submitNotify}>
                    <Input value={notifyContact} onChange={(e) => setNotifyContact(e.target.value)} placeholder="Email or WhatsApp number" className="h-10" data-testid="notify-contact" required />
                    <label className="flex items-start gap-2 text-xs text-stone-600">
                      <input type="checkbox" checked={notifyConsent} onChange={(e) => setNotifyConsent(e.target.checked)} required className="mt-0.5 accent-[#C9A227]" data-testid="notify-consent" />
                      <span>Notify me when it's back — I agree to be contacted about this piece.</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <Button type="submit" variant="outline" className="rounded-full btn-press" data-testid="notify-submit">Notify me</Button>
                      <button type="button" className="text-xs text-stone-500 underline" onClick={() => setSelected({})} data-testid="clear-selection">Clear selection</button>
                    </div>
                  </form>
                )}
              </div>
            )}
            {product.computed && variant?._tier === "made_to_order" && (
              <p className="text-xs font-mono text-amber-700 bg-amber-50 border border-amber-200 inline-block px-2 py-1" data-testid="mto-lead">
                MADE TO ORDER — {(product.computed.mto_lead_time || "").toUpperCase()}
              </p>
            )}
            {product.made_to_order_days > 0 && (
              <p className="text-xs font-mono text-amber-700 bg-amber-50 border border-amber-200 inline-block px-2 py-1" data-testid="made-to-order">
                MADE TO ORDER — CRAFTED IN {product.made_to_order_days} DAYS + INSURED DELIVERY
              </p>
            )}
            {variant?.sku && <p className="text-xs font-mono text-stone-400">SKU: {variant.sku} · NET {variant.net_weight}g · GROSS {variant.gross_weight}g</p>}
            {variant?.pricing?.cad_weight && (
              <div className="mt-2 border border-stone-200 bg-stone-50 px-3 py-2.5" data-testid="approx-weight">
                <p className="text-xs text-stone-700">Approx. metal weight: <span className="font-mono font-semibold">{variant.pricing.cad_weight} g</span> ({purity === "925" ? "925 silver" : `${purity} gold`})</p>
                <p className="text-[11px] text-stone-500 mt-1 leading-relaxed">
                  Each piece is handcrafted, so the finished weight can vary slightly (within {variant.pricing.weight_tolerance_pct || 3}%).
                  Your price is calculated on a standard billing weight and does not change with minor variation — the exact weight of your finished piece is recorded on its certificate and invoice.
                </p>
              </div>
            )}
          </div>

          {/* Personalisation */}
          {product.personalised && (
            <div className="mt-6">
              <Personalisation config={product.personalisation} value={pers} onChange={setPers} />
            </div>
          )}

          {/* Paid packaging add-on — order-level, printed as a line for packing */}
          {(config?.packaging_options || []).length > 0 && (
            <div className="mt-6" data-testid="packaging-picker">
              <p className="eyebrow mb-2">PACKAGING</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {config.packaging_options.map((p) => {
                  const active = packaging?.id === p.id;
                  return (
                    <button key={p.id} onClick={() => setPackaging(active ? null : p)}
                      className={`border rounded-lg p-2.5 text-left transition-colors ${active ? "border-brand bg-brand-light/40" : "border-stone-200 bg-white hover:border-brand/50"}`}
                      data-testid={`packaging-${p.id}`}>
                      {p.image && <img src={p.image} alt="" className="h-14 w-full object-cover rounded mb-2" loading="lazy" />}
                      <p className="text-xs font-medium">{p.name}</p>
                      <p className="text-[11px] font-mono text-stone-500">{p.price ? `+${inr(p.price)}` : "Free"}</p>
                    </button>
                  );
                })}
              </div>
              {packaging && <p className="text-[11px] text-stone-500 mt-2">Gift orders never show prices on the printed invoice.</p>}
            </div>
          )}

          {/* Qty + CTA */}
          <div className="flex items-center gap-3 mt-6">
            <div className="flex items-center border border-stone-300 h-11 bg-white">
              <button className="px-3 h-full hover:bg-stone-100" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" aria-label="Decrease quantity"><Minus size={15} /></button>
              <span className="w-10 text-center font-mono" data-testid="qty-value">{qty}</span>
              <button className="px-3 h-full hover:bg-stone-100" onClick={() => setQty(Math.min(variant?.stock || 99, qty + 1))} data-testid="qty-plus" aria-label="Increase quantity"><Plus size={15} /></button>
            </div>
            <Button className="flex-1 h-11 bg-brand hover:bg-brand-2 text-ivory btn-press rounded-full border border-gold/50" disabled={!inStock} onClick={() => tryAdd(false)} data-testid="add-to-cart-btn">
              {variant?._tier === "made_to_order"
                ? `${config?.mto_cta_label || "Pre-order"}${product.computed?.mto_lead_time ? ` — ${product.computed.mto_lead_time}` : ""}`
                : "Add to Cart"}
            </Button>
            <Button variant="outline" className="flex-1 h-11 btn-press rounded-full border-brand/40 text-brand hover:bg-brand-light" disabled={!inStock} onClick={() => tryAdd(true)} data-testid="buy-now-btn">
              Buy Now
            </Button>
            <Button variant="outline" size="icon" className="h-11 w-11 rounded-none border-stone-300" onClick={() => toggle(product)} data-testid="pdp-wishlist-btn" aria-label="Wishlist">
              <Heart size={18} className={isWishlisted(product.id) ? "text-brand" : ""} fill={isWishlisted(product.id) ? "currentColor" : "none"} />
            </Button>
            <Button variant="outline" size="icon" className="h-11 w-11 rounded-none border-stone-300" onClick={share} data-testid="pdp-share-btn" aria-label="Share">
              <Share2 size={17} />
            </Button>
          </div>
          {!breakup?.mrp && (
          <p className="text-[11px] text-stone-500 mt-2 flex items-center gap-1.5" data-testid="price-lock-notice">
            <ShieldCheck size={12} className="text-gold" /> Price held for {config?.price_lock_minutes || 30} minutes once added to cart.
          </p>
          )}

          {/* Delivery */}
          <div className="mt-7 border border-stone-200 bg-white p-4 rounded-lg">
            <p className="text-sm font-medium flex items-center gap-2"><Truck size={16} /> Estimated delivery</p>
            <div className="flex gap-2 mt-3">
              <Input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Enter PIN code" inputMode="numeric" className="h-10" data-testid="pincode-input" />
              <Button variant="outline" onClick={checkPincode} disabled={pincode.length !== 6 || checking} className="h-10 btn-press" data-testid="pincode-check-btn">
                {checking ? "…" : "Check"}
              </Button>
            </div>
            {delivery && !delivery.available && (
              <p className="text-sm mt-2 text-red-600" data-testid="pincode-result">Sorry, delivery not available to {pincode}</p>
            )}
            {delivery?.available && (
              <div className="mt-2" data-testid="pincode-result">
                {delivery.promise?.eta_by ? (
                  <p className="text-sm font-medium text-emerald-700 flex items-center gap-1.5" data-testid="delivery-receive-by">
                    <Check size={15} /> Receive by {delivery.promise.eta_by}
                  </p>
                ) : (
                  <p className="text-sm text-emerald-700">Insured delivery to {pincode}</p>
                )}
                {delivery.promise && (
                  <p className="text-xs text-stone-600 mt-1" data-testid="delivery-promise">
                    {delivery.promise.label ? `${delivery.promise.label} · ` : ""}Arrives {delivery.promise.eta}
                    {delivery.promise.shipping_fee ? ` · shipping ${inr(delivery.promise.shipping_fee)}` : " · free insured shipping"}
                    {product.made_to_order_days ? " · includes make-to-order time" : ""}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="mt-7">
            <p className="eyebrow mb-2">THE PIECE</p>
            <p className="text-stone-700 leading-relaxed" data-testid="product-description">{product.description}</p>
          </div>
        </div>
      </div>

      {/* Specification tables */}
      {(isRing || (product.stones?.length > 0) || (variant?.stones?.length > 0)) && (
        <section className="mt-14 grid md:grid-cols-2 gap-4" data-testid="spec-tables">
          <div className="border border-stone-200 bg-white rounded-lg overflow-hidden">
            <p className="eyebrow px-4 py-3 border-b border-stone-100 bg-stone-50">{product.metal === "silver" ? "SILVER DETAILS" : "GOLD DETAILS"}</p>
            <dl className="divide-y divide-stone-100 text-sm">
              {[
                ["Purity", purityLabel(purity, config)],
                ["Material", config?.material_copy?.[product.metal === "silver" ? "silver" : "gold"] || "—"],
                ["Colour", colourVariantLabel(purity, colour, config)],
                ["Gross weight", `${variant?.gross_weight ?? "—"} g`],
                ["Net metal weight", `${variant?.net_weight ?? "—"} g`],
                ["Craft note", "Handcrafted — finished weight can vary slightly; billed weight is fixed"],
                ...(product.metal === "gold" && config?.legal?.bis_reg
                  ? [["Hallmark", "BIS with HUID"],
                     ["HUID", variant?.hallmark && variant.hallmark !== "BIS HUID" ? variant.hallmark : "Engraved on the piece"]]
                  : [["Purity mark", product.metal === "silver" ? "925 sterling stamp on the piece" : "Stamped on the piece"]]),
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-2.5"><dt className="text-stone-500">{k}</dt><dd className="font-medium text-right">{v}</dd></div>
              ))}
            </dl>
          </div>
          <div className="border border-stone-200 bg-white rounded-lg overflow-hidden">
            <p className="eyebrow px-4 py-3 border-b border-stone-100 bg-stone-50">{product.metal === "silver" ? "STONE DETAILS" : "DIAMOND DETAILS"}</p>
            <dl className="divide-y divide-stone-100 text-sm">
              {[
                ["Type", stoneLabel(product.metal)],
                ["Total carats", stone ? `${stone.carats} ct` : "—"],
                ["Number of stones", variant?.stones?.[0]?.count || "—"],
                ["Shape", variant?.stones?.[0]?.shape || "—"],
                ["Setting", attrs.setting || "—"],
                ["Clarity · Colour grade", `${attrs.clarity || "VS"} · ${attrs.colour_grade || "E–F"}`],
                ["Certification", attrs.certification || "SGL"],
                ...(variant?.stones || []).filter((s) => s.species).map((s) => [
                  "Gemstone disclosure",
                  `${({ natural: "Natural", "lab-grown": "Lab-grown", simulant: "Simulant" }[s.origin] || "Origin unstated")} ${s.species}${s.treatment === "none" ? ", untreated" : s.treatment ? `, ${s.treatment}${["irradiated", "heat-treated"].includes(s.treatment) ? " (standard industry treatment)" : ""}` : ""}${s.cert_body ? ` · ${s.cert_body} certified` : ""}`,
                ]),
                ...(variant?.stones || []).filter((s) => s.traditional_association).map((s) => [
                  "Traditionally associated with",
                  `${s.traditional_association} — from tradition and belief, offered for interest; not a claim of any outcome.`,
                ]),
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between px-4 py-2.5"><dt className="text-stone-500">{k}</dt><dd className="font-medium text-right">{v}</dd></div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* Certification block — lab-diamond certification applies to gold pieces */}
      {isRing && product.metal !== "silver" && (
        <section className="mt-6 border border-gold/30 bg-brand-light/40 rounded-lg p-5 flex gap-4" data-testid="certification-block">
          <BadgeCheck size={28} className="text-gold shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Certified by {attrs.certification || "SGL"} (Solitaire Gemmological Laboratories)</p>
            <p className="text-sm text-stone-600 mt-1 leading-relaxed">
              Every Siyaara diamond ring ships with an independent laboratory certificate covering the stones' identity as
              lab-grown diamonds, their total carat weight, clarity and colour grade, and the gold purity of the mount.
              The certificate number is printed on your invoice, so your paperwork and your piece always travel together.
            </p>
          </div>
        </section>
      )}

      {/* Legal metrology: mandatory supplier declaration on every listing */}
      {product.supplier?.marketed_by && (
        <section className="mt-10 max-w-3xl border border-stone-200 bg-white rounded-lg p-5" data-testid="supplier-info">
          <p className="eyebrow mb-3">SUPPLIER INFORMATION</p>
          <dl className="text-sm space-y-1.5">
            <div className="flex justify-between gap-6"><dt className="text-stone-500 shrink-0">Marketed by</dt><dd className="font-medium text-right">{product.supplier.marketed_by}</dd></div>
            {product.supplier.address && <div className="flex justify-between gap-6"><dt className="text-stone-500 shrink-0">Registered address</dt><dd className="font-medium text-right">{product.supplier.address}</dd></div>}
            <div className="flex justify-between gap-6"><dt className="text-stone-500 shrink-0">Country of origin</dt><dd className="font-medium text-right">{product.supplier.country_of_origin}</dd></div>
            <div className="flex justify-between gap-6"><dt className="text-stone-500 shrink-0">Net quantity</dt><dd className="font-medium text-right">{product.supplier.net_quantity}</dd></div>
            <div className="flex justify-between gap-6"><dt className="text-stone-500 shrink-0">Commodity</dt><dd className="font-medium text-right">{product.supplier.generic_name}</dd></div>
            {(product.supplier.consumer_care_name || product.supplier.consumer_care_phone || product.supplier.consumer_care_email) && (
              <div className="flex justify-between gap-6"><dt className="text-stone-500 shrink-0">Consumer care</dt>
                <dd className="font-medium text-right">{[product.supplier.consumer_care_name, product.supplier.consumer_care_phone, product.supplier.consumer_care_email].filter(Boolean).join(" · ")}</dd></div>
            )}
          </dl>
        </section>
      )}

      {/* Product FAQ — library-driven, tag-assigned (RING_FAQS = fallback copy) */}
      {(() => {
        const faqs = (product.faqs?.length ? product.faqs.map((f) => [f.question, f.answer])
          : (isRing ? RING_FAQS : []));
        if (!faqs.length) return null;
        return (
        <section className="mt-14 max-w-3xl" data-testid="product-faq">
          <p className="eyebrow">QUESTIONS, ANSWERED</p>
          <h2 className="font-heading text-2xl font-semibold tracking-tight mt-1 mb-4">About this {isRing ? "ring" : "piece"}</h2>
          <div className="divide-y divide-stone-200 border-y border-stone-200">
            {faqs.map(([q, a], i) => (
              <div key={q}>
                <button className="w-full flex items-center justify-between py-4 text-left text-sm font-medium hover:text-brand transition-colors focus-visible:outline-2 focus-visible:outline-brand"
                  onClick={() => setFaqOpen(faqOpen === i ? -1 : i)} aria-expanded={faqOpen === i} data-testid={`faq-q-${i}`}>
                  {q}
                  <ChevronDown size={16} className={`shrink-0 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
                </button>
                {faqOpen === i && <p className="pb-4 text-sm text-stone-600 leading-relaxed animate-fade-up" data-testid={`faq-a-${i}`}>{a}</p>}
              </div>
            ))}
          </div>
        </section>
        );
      })()}

      <Reviews product={product} />

      {/* Complete the look */}
      {product.related?.length > 0 && (
        <section className="mt-14">
          <p className="eyebrow">COMPLETE THE LOOK</p>
          <h2 className="font-heading text-2xl font-semibold tracking-tight mt-1 mb-6">Pairs beautifully with</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {product.related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* You may also like (fallback to recents slot when no curated related) */}
      {recents.length > 0 && (
        <section className="mt-14">
          <p className="eyebrow">RECENTLY VIEWED</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6" data-testid="recently-viewed">
            {recents.map((p) => (
              <Link key={p.id} to={`/products/${p.slug}`} className="group bg-white border border-stone-200 hover:border-stone-400 transition-colors">
                <div className="aspect-square overflow-hidden bg-stone-50">
                  <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium line-clamp-1">{p.title}</p>
                  <p className="text-xs text-stone-500 font-mono mt-0.5">{p.purity} {p.metal}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Mobile sticky buy bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-stone-200 px-4 py-3 flex items-center gap-3" data-testid="mobile-buy-bar">
        <div className="min-w-0">
          <p className="font-heading font-semibold truncate">{inr(displayFinal)}</p>
          <p className="text-[10px] font-mono text-stone-500 truncate">{purity}{colour ? ` · ${colour}` : ""}{ringSize && ringSize !== "later" ? ` · Size ${ringSize}` : ""}</p>
        </div>
        <Button className="flex-1 h-11 bg-brand text-ivory rounded-full btn-press" disabled={!inStock} onClick={() => tryAdd(false)} data-testid="mobile-add-to-cart">
          {inStock ? (variant?._tier === "made_to_order" ? (config?.mto_cta_label || "Pre-order") : "Add to Cart") : "Out of stock"}
        </Button>
      </div>

      {/* Zoom lightbox */}
      <Dialog open={zoom} onOpenChange={setZoom}>
        <DialogContent className="sm:max-w-3xl p-2" aria-describedby={undefined} data-testid="image-zoom-dialog">
          <img src={images[imgIdx]} alt={product.title} className="w-full object-contain max-h-[80vh]" />
        </DialogContent>
      </Dialog>

      {/* Size guide */}
      <Dialog open={sizeGuide} onOpenChange={setSizeGuide}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="size-guide-dialog">
          <DialogHeader><DialogTitle className="font-heading">Ring Size Guide</DialogTitle></DialogHeader>
          {isRing || product.category === "Rings" ? (
            <div className="space-y-5 text-sm">
              <div>
                <p className="font-medium mb-1.5">Measure at home — two easy ways</p>
                <ol className="list-decimal ml-4 space-y-1.5 text-stone-600 text-[13px]">
                  <li><b>Thread method:</b> wrap a strip of paper or thread snugly around the base of your finger, mark where it meets, and measure the length in millimetres. Match that circumference in the chart below.</li>
                  <li><b>Existing-ring method:</b> take a ring that fits well and measure its inner diameter edge to edge. Match that diameter in the chart.</li>
                </ol>
                <p className="text-[12px] text-stone-500 mt-2">Measure at the end of the day when fingers are at their largest, and size up if you're between sizes.</p>
              </div>
              <div className="border border-stone-200 rounded-lg overflow-hidden" data-testid="size-chart">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50 text-stone-500 font-mono">
                    <tr><th className="py-2 px-2 text-left">INDIA</th><th className="text-left">DIAMETER (MM)</th><th className="text-left">CIRC. (MM)</th><th className="text-left">US</th><th className="text-left">UK</th></tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100 font-mono">
                    {(product.available_sizes || []).map((s) => {
                      const r = ringRow(Number(s));
                      return (
                        <tr key={s} className={String(ringSize) === s ? "bg-brand-light" : ""}>
                          <td className="py-1.5 px-2 font-semibold">{s}</td><td>{r.d}</td><td>{r.c}</td><td>{r.us}</td><td>{r.uk}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-[10px] text-stone-400 px-2 py-2">US/UK columns are approximate conversions. Print at 100% scale and place an existing ring over the chart to double-check.</p>
              </div>
            </div>
          ) : (
            <ul className="text-sm space-y-2 text-stone-600">
              <li><span className="font-mono">16 in / 41 cm</span> — choker, sits at the collarbone</li>
              <li><span className="font-mono">18 in / 46 cm</span> — princess, most popular length</li>
              <li><span className="font-mono">20 in / 51 cm</span> — matinee, below the collarbone</li>
              <li className="pt-2 border-t">Bangles: <span className="font-mono">2.4 (5.7cm) · 2.6 (6.0cm) · 2.8 (6.4cm)</span> inner diameter</li>
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
